// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import * as Cookie from 'app/packages/cookie-utils';
import BrowserDetector from 'app/common/lib/BrowserDetector';
import getBannerPresentation from 'app/common/react/components/growth/unauth/open/hybrid/getBannerPresentation';
import InlineStyle from 'app/common/react/components/InlineStyle';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import type { State as InviteObjectType } from 'app/www-unauth/redux/reducers/inviteObject';
import type { State as ReduxState } from 'app/www-unauth/redux/reducers/types';
import UnauthBannerSignupModalLoader from 'app/common/react/components/growth/unauth/UnauthBanner/UnauthBannerSignupModalLoader';
import { FullPageSignupToggleType } from 'app/common/react/components/growth/unauth/signup/FullPageSignup/FullPageSignupToggleType';
import { inOpenModalExperience } from 'app/common/react/components/growth/unauth/open/hybrid/hybridExperimentUtils';
import { UNAUTH_ACTION_TYPE } from 'app/www-unauth/redux/reducers/modalBudget';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  HybridTiers,
  type HybridTierType,
} from 'app/common/react/components/growth/unauth/open/hybrid/hybridConstants';
import {
  SignupSource,
  type SignupTypesFlowType,
} from 'app/common/react/components/growth/unauth/signup/constants';
import { compose, type Dispatch } from 'redux';

const SCROLL_THROTTLE_TIMEOUT = 10;
const SCROLL_SESSION_COUNT_MULTIPLIER = 5;

export const MODAL_TYPE = {
  DEFAULT: 'default',
  DISMISSIBLE: 'dismissible',
};
export const BANNER_TYPE = {
  FULL_SCREEN_BANNER: 'fullScreenBanner',
  QUARTER_SCREEN_BANNER: 'quarterScreenBanner',
  NO_BANNER: 'noBanner',
};
const FULL_SCREEN_THRESHOLD = 800;

export type BannerFlowTypes = $Values<typeof BANNER_TYPE>;

type ModalFlowTypes = $Values<typeof MODAL_TYPE>;

export type BannerPresentation = $Shape<{
  bannerSize?: BannerFlowTypes,
  modalType?: ModalFlowTypes,
  signupSource?: SignupTypesFlowType,
}>;

type OwnProps = {|
  marketTier: HybridTierType,
  onHybridBannerTriggerFullscreen?: (boolean) => void,
  onSignupClick: () => void,
  onLoginClick: () => void,
  inviteObject?: InviteObjectType,
|};

type ReduxProps = {|
  facebookAutoLoginStatus: string,
|};

type Props = {|
  ...OwnProps,
  ...ReduxProps,
  dispatch: Dispatch<*>,
  seoUnauthExperiments: UnauthSeoExperiments,
|};

type State = {|
  scrollSessionCount: number,
  bannerSize: BannerFlowTypes,
  modalType: ModalFlowTypes,
  dismissed: boolean,
  signupSource: SignupTypesFlowType,
|};

export class UnauthHybridBanner extends React.Component<Props, State> {
  state = {
    scrollSessionCount: 0,
    bannerSize: 'noBanner',
    modalType: '',
    dismissed: false,
    signupSource: SignupSource.defaultSource,
  };

  componentDidMount() {
    window.addEventListener('scroll', this.onScrollHybridBanner);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { bannerSize, scrollSessionCount } = this.state;
    const { onHybridBannerTriggerFullscreen } = this.props;

    if (
      bannerSize === BANNER_TYPE.FULL_SCREEN_BANNER &&
      prevState.bannerSize === BANNER_TYPE.QUARTER_SCREEN_BANNER &&
      onHybridBannerTriggerFullscreen
    ) {
      onHybridBannerTriggerFullscreen(true);
    } else if (
      bannerSize === BANNER_TYPE.QUARTER_SCREEN_BANNER &&
      prevState.bannerSize === BANNER_TYPE.FULL_SCREEN_BANNER &&
      onHybridBannerTriggerFullscreen
    ) {
      onHybridBannerTriggerFullscreen(false);
    }
    if (scrollSessionCount === prevState.scrollSessionCount + 1) {
      this.props.dispatch({
        type: UNAUTH_ACTION_TYPE.SCROLL,
        payload: scrollSessionCount,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScrollHybridBanner);
  }

  onScrollHybridBanner = throttle((evt: SyntheticEvent<Document>) => {
    let scrollTop = 0;
    const { marketTier } = this.props;
    // wwu - the access for marketTier info
    const eventTarget: ?Document = (evt && evt.target: any);
    if (eventTarget && eventTarget.documentElement && eventTarget.documentElement.scrollTop > 0) {
      scrollTop = eventTarget.documentElement.scrollTop;
    } else if (eventTarget && eventTarget.body && eventTarget.body.scrollTop > 0) {
      scrollTop = eventTarget.body.scrollTop;
    }
    // wwu - 800 pixel is for current close
    const trackingUpperLimitThreshold = FULL_SCREEN_THRESHOLD * SCROLL_SESSION_COUNT_MULTIPLIER;
    const isSafari = BrowserDetector.isSafari();
    if (scrollTop < trackingUpperLimitThreshold) {
      const scrollSessionCount = Math.ceil(scrollTop / FULL_SCREEN_THRESHOLD);
      this.setState({ scrollSessionCount });
      const newState = getBannerPresentation(
        marketTier,
        this.state.scrollSessionCount,
        this.state.dismissed,
      );
      this.setState(newState);
    } else if (
      !Cookie.get(PCONST.LOCALSTORAGE_HAS_BEEN_SHOWN_LOGIN_MODAL_IN_OPEN) &&
      !inOpenModalExperience() &&
      !isSafari // Temp fix for BUG-92861, disable full screen modal on Safari
    ) {
      if (marketTier === HybridTiers.tier1) {
        this.setState({
          bannerSize: BANNER_TYPE.FULL_SCREEN_BANNER,
          modalType: MODAL_TYPE.DISMISSIBLE,
          signupSource: SignupSource.login,
        });
      } else {
        this.setState({
          bannerSize: BANNER_TYPE.FULL_SCREEN_BANNER,
          modalType: MODAL_TYPE.DEFAULT,
        });
      }
    }
  }, SCROLL_THROTTLE_TIMEOUT);

  handleDismissSignupModal = () => {
    this.setState({
      bannerSize: BANNER_TYPE.QUARTER_SCREEN_BANNER,
      dismissed: true,
    });
  };

  renderUnauthBannerSignupModal = () => {
    const { inviteObject } = this.props;
    const { modalType } = this.state;
    const canClose = modalType === MODAL_TYPE.DISMISSIBLE;
    const toggleType = canClose
      ? FullPageSignupToggleType.NOT_NOW_BUTTON
      : FullPageSignupToggleType.DEFAULT_TOGGLE;
    const invite = inviteObject && inviteObject.loadedState ? inviteObject.invite : null;
    const validInviter = invite ? invite.sender : null;

    return (
      <UnauthBannerSignupModalLoader
        inTierMarket
        canClose={canClose}
        handleDismissSignupModal={this.handleDismissSignupModal}
        hybridTier={this.props.marketTier}
        bannerSize={this.state.bannerSize}
        onSignupClick={this.props.onSignupClick}
        onLoginClick={this.props.onLoginClick}
        signupSource={this.state.signupSource}
        toggleType={toggleType}
        inviter={validInviter || null}
      />
    );
  };

  render() {
    if (this.props.facebookAutoLoginStatus === 'FB_AUTOLOGIN_AUTH_EXPIRED') {
      // Facebook two tap V2 should only be triggered when we are certain user has an pinterest account using facebook
      // but the authorization expired. Thus hide the banner.
      return null;
    }

    const isFullScreenBanner = this.state.bannerSize === BANNER_TYPE.FULL_SCREEN_BANNER;
    const isDismissibleModal = this.state.modalType === MODAL_TYPE.DISMISSIBLE;
    const notDismissed = !this.state.dismissed;

    const innerHtml =
      isFullScreenBanner && isDismissibleModal && notDismissed ? (
        <InlineStyle unsafeCSS="body { overflow: hidden }" />
      ) : null;

    const footerHtml = (
      <React.Fragment>
        {innerHtml}
        {this.renderUnauthBannerSignupModal()}
      </React.Fragment>
    );

    if (this.context.isBot) {
      return null;
    } else {
      return <div>{footerHtml}</div>;
    }
  }
}

const mapStateToProps = (state: ReduxState): ReduxProps => ({
  facebookAutoLoginStatus: state.facebookAutoLogin.facebookAutoLoginStatus,
});

// $FlowFixMe: new errors found from flow 0.96 upgrade
export default compose(connect(mapStateToProps))(withUnauthSeoExperiments(UnauthHybridBanner));

UnauthHybridBanner.contextTypes = {
  isBot: PropTypes.bool.isRequired,
};
